import { createTheme as createMuiTheme } from '@mui/material/styles' // keep @mui/material/styles to use default theme of @mui/material

const htmlFontSize = 14
const fontSize = 14

const muiTheme = createMuiTheme({ typography: { htmlFontSize, fontSize } })

const fontFamily = 'Arial, sans-serif'
const fontFamilyAlternative = 'Arial, sans-serif'

const breakpoints = {
	values: {
		// mobile
		xs: 0, // mobile small (iphone 5, iphone SE)
		xsm: 375, // mobile large (iphone X) -> custom breakpoint unsupported by Grid

		// tablet
		sm: 768, // tablet portrait
		md: 1024, // tablet landscape

		// desktop
		lg: 1280, // desktopHD
		xl: 1440, // (MacBook pro 13)
		xxl: 1920, // desktopFullHD -> custom breakpoint unsupported by Grid
	},
}

const globalTheme = createMuiTheme({
	breakpoints,
	direction: 'ltr',
	palette: {
		common: {
			black: '#141616',
			white: '#fff',
		},
		mode: 'light',
		primary: {
			main: '#141616',
			light: '#434445',
			dark: '#000000',
			contrastText: '#FFFFFF',
		},
		secondary: {
			main: '#005DFF',
			light: '#669EFF',
			dark: '#024ED0',
			contrastText: '#FFFFFF',
		},
		error: {
			main: '#D13131',
			light: '#D13131',
			dark: '#D13131',
			contrastText: '#fff',
		},
		warning: {
			light: '#FF7801',
			main: '#FF7801',
			dark: '#FF7801',
			contrastText: '#FFFFFF',
		},
		info: {
			light: '#FF7801',
			main: '#FF7801',
			dark: '#FF7801',
			contrastText: '#FFFFFF',
		},
		success: {
			light: '#75A53E',
			main: '#75A53E',
			dark: '#75A53E',
			contrastText: '#FFFFFF',
		},
		grey: {
			lighter: '#F7F7F7',
			light: '#E1E1E1',
			medium: '#B1B1B1',
			main: '#777777',
			dark: '#373737',
			disabled: '#E3E3E3',
			active: '#ECECEC',
		},
		contrastThreshold: 3,
		tonalOffset: 0.2,
		text: {
			primary: '#000000',
			secondary: '#005DFF',
			disabled: '#fafafa',
		},
		divider: 'rgba(0, 0, 0, 0.12)',
		background: {
			paper: '#FFFFFF',
			default: '#FFFFFF',
			header: '#FFFFFF',
		},
		action: {
			active: 'rgba(20, 22, 22, 0.54)',
			hover: 'rgba(20, 22, 22, 0.04)',
			hoverOpacity: 0,
			selected: 'rgba(20, 22, 22, 0.08)',
			selectedOpacity: 0.08,
			disabled: 'rgba(20, 22, 22, 0.8)',
			disabledBackground: 'rgba(20, 22, 22, 0.12)',
			disabledOpacity: 0.38,
			focus: 'rgba(20, 22, 22, 0.12)',
			focusOpacity: 0.12,
			activatedOpacity: 0.12,
			loading: 'rgba(255, 255, 255, 0.5)',
		},
		white: {
			main: '#ffffff',
		},
		black: {
			main: '#141616',
		},
		ghost: {
			hover: '#F6F6F6',
		},
		rating: {
			main: '#FF7801',
		},
		slider: {
			hover: '#373838',
			focus: '#2A2B2B',
			active: '#5A5C5C',
		},
		social: {
			facebook: '#0272E7',
			facebookHover: '#4b97e6',
		},
		footer: {
			bgTop: '#FFFFFF',
			bgMiddleTop: '#141616',
			bgMiddle: '#141616',
			bgMiddleBottom: '#141616',
			bgBottom: '#141616',
			border: '#373737',
			color: '#FFFFFF',
		},
	},
	typography: {
		fontFamily,
		fontFamilyAlternative,
		htmlFontSize,
		fontSize,
		fontWeightLight: 300,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,
		h1: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(24),
			lineHeight: 1,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(40),
			},
		},
		h2: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(22),
			lineHeight: 1.185,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(32),
			},
		},
		h3: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(20),
			lineHeight: 1.167,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(28),
			},
		},
		h4: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(18),
			lineHeight: 1.235,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(24),
			},
		},
		h5: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(16),
			lineHeight: 1.2,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(20),
			},
		},
		h6: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.15,
			letterSpacing: '0.2px',
			[`@media screen and (min-width: ${breakpoints.values.lg}px)`]: {
				fontSize: muiTheme.typography.pxToRem(18),
			},
		},
		subtitle1: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.75,
			letterSpacing: '0.2px',
		},
		subtitle2: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(16),
			lineHeight: 1.57,
			letterSpacing: '0.2px',
		},
		body1: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.6,
			letterSpacing: '0.2px',
		},
		body2: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.6,
			letterSpacing: '0.2px',
		},
		p: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.6,
			letterSpacing: '0.2px',
		},
		button: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(14),
			lineHeight: 1.3,
			letterSpacing: '0.2px',
		},
		caption: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(12),
			lineHeight: 1.4,
			letterSpacing: '0.2px',
		},
		overline: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(12),
			lineHeight: 2.66,
			letterSpacing: '0.2px',
			textTransform: 'uppercase',
		},
		headlinebig: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(90),
			lineHeight: 1,
			letterSpacing: '0.2px',
		},
		headlinemedium: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(86),
			lineHeight: 1,
			letterSpacing: '0.2px',
		},
		headlinesmall: {
			fontFamily,
			fontWeight: 700,
			fontSize: muiTheme.typography.pxToRem(78),
			lineHeight: 1,
			letterSpacing: '0.2px',
		},
		subheadline1: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(24),
			lineHeight: 1.2,
			letterSpacing: '0.2px',
		},
		subheadline2: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(20),
			lineHeight: 1.2,
			letterSpacing: '0.2px',
		},
		checkbox: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(12),
			lineHeight: '16px',
			letterSpacing: '0.2px',
		},
		label: {
			fontFamily,
			fontWeight: 400,
			fontSize: muiTheme.typography.pxToRem(12),
			lineHeight: 1.4,
			letterSpacing: '0.2px',
			color: '#777777',
		},
	},
	shape: {
		borderRadius: 4,
	},
	transitions: {
		easing: {
			easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
			easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
			easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
			sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
		},
		duration: {
			shortest: 150,
			shorter: 200,
			short: 250,
			standard: 300,
			complex: 375,
			enteringScreen: 225,
			leavingScreen: 195,
		},
	},
	zIndex: {
		mobileStepper: 1000,
		speedDial: 1050,
		appBar: 1100,
		drawer: 1200,
		modal: 1300,
		snackbar: 1400,
		tooltip: 1500,
	},
	shadows: [
		'', // elevation 0
		'0 0 1px 0 rgba(10,22,70,0.06),0 1px 1px 0 rgba(10,22,70,0.1)', // elevation 1
		'0 0 1px 0 rgba(10,22,70,0.06),0 3px 3px -1px rgba(10,22,70,0.1)', // elevation 2
		'0 0 1px 0 rgba(10,22,70,0.06),0 6px 6px -1px rgba(10,22,70,0.1)', // elevation 3
		'0 0 1px 0 rgba(10,22,70,0.06),0 16px 16px -1px rgba(10,22,70,0.1)', // elevation 4
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 5
		'0 0 44px 0 rgba(0,0,0,0.13)', // elevation 6 (FOR MODAL)
		'0px 33px 54px 0 rgb(0 0 0 / 5%)', // elevation 7
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 8
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 9
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 10
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 11
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 12
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 13
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 14
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 15
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 16
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 17
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 18
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 19
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 20
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 21
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 22
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 23
		'0 0 1px 0 rgba(10,22,70,0.06),0 32px 40px -2px rgba(10,22,70,0.1)', // elevation 24
	],
	scrollbar: `
		overflow-y: auto;
		scrollbar-width: auto;
		scrollbar-color: #777777 #F7F7F7; // thumb, track

		::-webkit-scrollbar {
			width: 7px;
		}

		//track
		::-webkit-scrollbar-track {
			background: #F7F7F7; // grey.lighter
			border-radius: 3.5px;
		}

		//handle
		::-webkit-scrollbar-thumb {
			background: #777777; // grey.main
			border-radius: 3.5px;
		}

		//handle on hover
		::-webkit-scrollbar-thumb:hover {
			background: #373737; // grey.dark
		}
	`,
	scrollbarJsx: {
		scrollbarWidth: 'auto',
		scrollbarColor: '#777777 #F7F7F7', // thumb, track

		'::-webkit-scrollbar': {
			width: 7,
		},
		//track
		'::-webkit-scrollbar-track': {
			background: '#F7F7F7', // grey.lighter
			borderRadius: 3.5,
		},
		//handle
		'::-webkit-scrollbar-thumb': {
			background: '#777777', // grey.main
			borderRadius: 3.5,
		},
		//handle on hover
		'::-webkit-scrollbar-thumb:hover': {
			background: '#373737', // grey.dark
		},
	},
})

export default globalTheme
export { muiTheme }
